import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { useGlobalApiAccessToken } from './GlobalApiAccessTokenProvider';

type Gate = {
  promise: Promise<void>;
  resolve: () => void;
  settled: boolean;
};

let gate: Gate | null = null;

const ensureGate = () => {
  if (!gate || gate.settled) {
    let resolve: () => void = () => {};
    const promise = new Promise<void>((done) => {
      resolve = done;
    });
    gate = { promise, resolve, settled: false };
  }
  return gate;
};

const releaseBootstrap = () => {
  if (!gate || gate.settled) return;
  gate.settled = true;
  gate.resolve();
};

const useSessionPending = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const accessToken = useGlobalApiAccessToken();
  const hasToken = Boolean(accessToken?.globalApiAccessToken);
  return isLoading || (isAuthenticated && !hasToken);
};

// The gate promise is resolved from a component outside the boundary. A
// suspended render never commits, so an effect inside it would not run.
export const ReleaseBootstrap = () => {
  const sessionPending = useSessionPending();
  useEffect(() => {
    if (!sessionPending) releaseBootstrap();
  }, [sessionPending]);
  return null;
};

export const BootstrapSuspense = ({ children }: PropsWithChildren) => {
  const sessionPending = useSessionPending();
  if (sessionPending) throw ensureGate().promise;
  releaseBootstrap();
  return children;
};
